// Modules
import { useEffect, useState } from "react";
import { getIcons } from "../../../../helpers/iconCache";

// Components
import { DishResize } from "../room/resize/dish.layout";

// Properties
import { SelectorDevicesProperties } from "./selectorDevices.properties";
import { SelectorDeviceType } from "./selectorDevices.types";

// Styles
import "./selector.styles.scss";
import { useTranslation } from "react-i18next";
import { appEventEmitter } from "../../../../context/EventContext";

/**
 * Helper function to check if two labels match considering comma-separated parts
 * @param filterLabel The filter label to check
 * @param deviceLabel The device label to check against
 * @returns true if labels match
 */
const isLabelMatch = (filterLabel: string, deviceLabel: string): boolean => {
  if (!filterLabel || !deviceLabel) return false;

  // Split both labels by comma and trim each part
  const filterLabelParts = filterLabel.split(',').map(part => part.trim().toLowerCase());
  const deviceLabelParts = deviceLabel.split(',').map(part => part.trim().toLowerCase());

  // Check if any part of the filter label matches any part of the device label
  // or if the whole filter label is included in the device label
  return filterLabelParts.some(filterPart =>
    deviceLabelParts.some(devicePart => devicePart.includes(filterPart))
  ) || deviceLabel.toLowerCase().includes(filterLabel.toLowerCase());
};

/**
 * Selector Devices Component
 * @description Component to select devices
 * @param {SelectorDevicesProperties} properties
 * @returns {JSX.Element}
 */
export function SelectorDevicesComponent(
  properties: SelectorDevicesProperties
) {
  // translation function
  const { t } = useTranslation();

  // state to avoid multiple clicks in media selector
  const [selected, setSelected] = useState(false);

  // array of devices
  const [cameras, setCameras] = useState<SelectorDeviceType[]>([]);

  // get cameras from backend
  const getCameras = async () => {
    const groups = properties.groupsData;
    if (Array.isArray(groups) && groups.length > 0) {
      const allDevicesSet = new Map<string, SelectorDeviceType>();

      for (const group of groups) {
        for (const groups_devices of group.groups_devices) {
          allDevicesSet.set(groups_devices.device.id, groups_devices.device);
        }
      }

      // list of devices
      const camerasList = Array.from(allDevicesSet.values());
      // check is validateIsActive
      const camerasActives: any = [];
      for (const cameraSelect of camerasList) {
        // check if device is in properties.localScreams
        const active = await validateIsActive(cameraSelect);

        // device used state
        let used = false;

        if (cameraSelect?.filter?.label) {
          // search in local streams
          const localStream = properties.localScreams.find((stream) => {
            return stream.getTracks().find((track) => {
              if (!cameraSelect?.filter?.label) return false;
              return isLabelMatch(cameraSelect.filter.label, track.label);
            });
          });

          // if local stream exist, device is used
          if (localStream) used = true;
        }

        // add camera to list
        active &&
          camerasActives.push({
            ...cameraSelect,
            used: used,
          });
      }

      const finalCameras: SelectorDeviceType[] = [];

      const camerasIds = camerasActives.map((camera: SelectorDeviceType) => camera.id);

      // Filter out cameras that already have icons in the cache
      const camerasToFetch = camerasIds.filter((id: string) => !properties.devicesIconCache[id]);

      // Only fetch icons that aren't in the cache
      if (camerasToFetch.length > 0) {
        // Fetch from IndexedDB cache / API via iconCache
        const newIcons = await getIcons(camerasToFetch, 'device');

        // Update the in-memory icon cache with new icons
        properties.setDevicesIconCache((prev: Record<string, string>) => ({
          ...prev,
          ...newIcons,
        }));

        // Assign icons from cache or from new fetches
        camerasActives.forEach((camera: SelectorDeviceType) => {
          finalCameras.push({
            ...camera,
            // Primero busca en los iconos recién obtenidos, luego en el caché
            icon: newIcons[camera.id] || properties.devicesIconCache[camera.id] || '',
          });
        });
      } else {
        // Assign icons from cache
        camerasActives.forEach((camera: SelectorDeviceType) => {
          finalCameras.push({
            ...camera,
            icon: properties.devicesIconCache[camera.id] || '',
          });
        });
      }

      // set cameras
      setCameras(finalCameras);
      properties.setHasDevicesBeenFetched(true);
    }
  };


  // get cameras on load
  useEffect(() => {
    getCameras();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.groupsData]);

  // validate if device is active
  const validateIsActive = async (camera: any) => {
    if ("label" in camera.filter) {
      // get all enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // search device with label - handle comma-separated labels better
      const device = devices.find((device) => {
        if (!("label" in camera.filter)) return false;
        return isLabelMatch(camera.filter.label, device.label);
      });

      // if device exist, return true
      if (device) return device;
    } else {
      // get stream directly with getUserMedia for validation
      try {
        const stream = await navigator.mediaDevices.getUserMedia(camera.filter);
        if (stream) {
          // Stop the stream immediately since it's just for validation
          stream.getTracks().forEach(track => track.stop());
          return stream;
        }
      } catch (error) {
        console.warn('Device validation failed:', error);
      }
    }
    return undefined;
  };

  return (
    <>
      {properties.canBeDisplayed &&
        cameras.map((camera) => {
          return (
            <div
              className="camera"
              style={{
                backgroundColor: camera.used ? "#b7bffc" : "#fff",
                borderStyle: "solid",
                borderColor: camera.used ? "#2f49ff" : "#fff",
              }}
              key={camera.id}
              onClick={async () => {
                // avoid multiple clicks
                if (selected) return;

                // set selected state
                setSelected(true);

                if ("label" in camera.filter) {
                  // search again in enumerate devices
                  const devices = await navigator.mediaDevices.enumerateDevices();

                  // label contain camera.filter.label - handle comma-separated labels
                  const device = devices.find((device) => {
                    if (!("label" in camera.filter)) return false;
                    return isLabelMatch(camera.filter.label, device.label);
                  });

                  if (device) {
                    // Check if device is being used
                    const used = properties.localScreams.find((stream) => {
                      return stream.getTracks().find((track) => {
                        return isLabelMatch(device.label, track.label);
                      });
                    });

                    if (used) {
                      properties.removeStream?.(
                        properties.localScreams.indexOf(used)
                      );

                      await appEventEmitter.emit("device:stopped", {
                        action: "stop",
                        resource: "device",
                        agent: used.id,
                        event_phase: "end",
                        user: properties.user,
                        device: `${camera.name}-${camera.model}`,
                        room: properties.room,
                      });

                      setSelected(false);
                      properties.setScene?.(undefined);
                      return;
                    }

                    // Todos los dispositivos en este selector son médicos
                    // Usar configuración médica optimizada para preservar frecuencias bajas
                    let stream: MediaStream | undefined;

                    if (camera.filter.audio && !camera.filter.video) {
                      // Dispositivo solo de audio (estetoscopio), usar configuración médica
                      console.log('🩺 Obteniendo stream médico de audio para:', device.label);
                      stream = await properties.deviceInstance.getMedicalDeviceStream(
                        device.deviceId,
                        {
                          preserveLowFrequencies: true,
                          highQualityAudio: true,
                          disableProcessing: true,
                          sampleRate: 48000
                        }
                      );
                    } else {
                      // Dispositivo médico con video o mixto, aplicar configuración médica optimizada
                      console.log('🏥 Obteniendo stream médico con video para:', device.label);
                      stream = await navigator.mediaDevices.getUserMedia({
                        video: camera.filter.video
                          ? { deviceId: { exact: device.deviceId } }
                          : false,
                        audio: camera.filter.audio
                          ? { deviceId: { exact: device.deviceId } }
                          : false,
                      });
                    }

                    const manualId = camera.manual_id ?? "";

                    // add stream
                    if (stream) {
                      properties.addStream?.(
                        stream,
                        { type: "med.device" },
                        { manual_id: manualId }
                      );

                      await appEventEmitter.emit("device:started", {
                        action: "start",
                        resource: "device",
                        agent: stream.id,
                        event_phase: "start",
                        user: properties.user,
                        device: `${camera.name}-${camera.model}`,
                      });
                    }
                  }
                } else {
                  // Dispositivo médico con filtro personalizado
                  console.log('🏥 Obteniendo stream médico con filtro personalizado para:', camera.name);

                  // Aplicar configuración médica si el filtro incluye audio
                  let medicalFilter = { ...camera.filter };
                  if (medicalFilter.audio && typeof medicalFilter.audio === 'object') {
                    medicalFilter.audio = {
                      ...medicalFilter.audio,
                      // Configuración médica para preservar frecuencias bajas
                      echoCancellation: false,
                      noiseSuppression: false,
                      autoGainControl: false,
                      sampleRate: 48000,
                      // @ts-ignore - Propiedades experimentales para dispositivos médicos
                      googEchoCancellation: false,
                      googNoiseSuppression: false,
                      googAutoGainControl: false,
                      googHighpassFilter: false,
                    };
                  } else if (medicalFilter.audio === true) {
                    medicalFilter.audio = {
                      echoCancellation: false,
                      noiseSuppression: false,
                      autoGainControl: false,
                      sampleRate: 48000,
                      // @ts-ignore - Propiedades experimentales para dispositivos médicos
                      googEchoCancellation: false,
                      googNoiseSuppression: false,
                      googAutoGainControl: false,
                      googHighpassFilter: false,
                    };
                  }

                  const stream = await navigator.mediaDevices.getUserMedia(medicalFilter);
                  if (stream)
                    properties.addStream?.(
                      stream,
                      { type: "med.device" },
                      { manual_id: camera.manual_id ?? "" }
                    );
                }

                // resize dish cameras
                new DishResize(
                  "DishScenary",
                  "DishScenary",
                  "DishVideoComponent"
                ).resize();

                // close selector
                properties.setScene?.(undefined);
              }}
            >
              {
                // hide add icon if device is used
                <i className={camera.used ? "las la-times" : "las la-plus"}></i>
              }
              <div className="name">
                {camera.name}
              </div>
              <div
                className="image"
                style={{
                  backgroundColor: camera.used ? "#b7bffc" : "#fff",
                  backgroundImage: camera.icon ? `url(${camera.icon})` : "none",
                }}
              />
            </div>
          );
        })}
      {cameras.length === 0 && (
        <div
          className="no-camera"
          style={{
            color: "#fff",
            flex: 1,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {t("selector.empty")}
        </div>
      )}
    </>
  );
}
